import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BadgeCheckIcon, FileTextIcon, MessageSquareIcon, PenLineIcon, PencilIcon, PlayIcon, UsersIcon } from 'lucide-react';
import { useNotes } from '../../contexts/NotesContext';
import { Panel } from '../ui/Panel';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Meter } from '../ui/Meter';
import { Tabs } from '../ui/Tabs';
import { EmptyState } from '../ui/EmptyState';
import { ReportControls } from '../trust/ReportControls';
import { QuizRunner } from './QuizRunner';
import { learningFor } from '../../data/learning';
import { percent } from '../../utils/format';
import type { Course } from '../../types/academic';
const TABS = ['Materials', 'Lectures', 'Quizzes', 'Study groups', 'Discussion'] as const;
export function CourseWorkspace({
  course
}: {
  course: Course;
}) {
  const [tab, setTab] = useState<(typeof TABS)[number]>('Materials');
  const [activeQuizId, setActiveQuizId] = useState<string | null>(null);
  const learning = learningFor(course.code);
  const {
    createNote
  } = useNotes();
  const navigate = useNavigate();
  if (!learning) {
    return <div className="rounded-card border border-line bg-surface" data-id="element-2824" data-mp="3mmege1p2v8r6">
        <EmptyState icon={<FileTextIcon className="h-4 w-4" data-id="element-2826" data-mp="3hnbayg56r0io" />} title="No learning content yet" description={`${course.lecturer} has not published materials for ${course.code}. You can still add your own notes, which stay marked as entered by you.`} action={<Button variant="secondary" data-id="element-2827" data-mp="2p076qddx2q7y">Add your own material</Button>} data-id="element-2825" data-mp="3mlmq96e9238a" />
      </div>;
  }
  const topicsDone = learning.topics.filter(topic => topic.complete).length;
  const progress = percent(topicsDone, learning.topics.length);
  const lectureMinutes = learning.lectures.reduce((total, item) => total + item.durationMinutes, 0);
  const watchedMinutes = learning.lectures.reduce((total, item) => total + item.watchedMinutes, 0);
  const scored = learning.quizzes.filter(quiz => quiz.scored && quiz.bestScore !== null);
  const quizAverage = scored.length > 0 ? Math.round(scored.reduce((total, quiz) => total + (quiz.bestScore ?? 0), 0) / scored.length) : null;
  const reviewed = learning.materials.filter(material => material.reviewed).length;
  const activeQuiz = learning.quizzes.find(quiz => quiz.id === activeQuizId);
  const nextQuiz = learning.quizzes.find(quiz => quiz.dueLabel);
  return <div className="space-y-4" data-id="element-2828" data-mp="2hgqbcsshzz5">
      <div className="rounded-card border border-brand-700 bg-surface px-5 py-4" data-id="element-2829" data-mp="7hl6f1pscsou">
        <div className="flex flex-wrap items-start justify-between gap-4" data-id="element-2830" data-mp="chr16qrcqvb5">
          <div className="min-w-0" data-id="element-2831" data-mp="cfmobjbh6lyq">
            <h3 className="text-[15px] font-semibold text-ink" data-id="element-2832" data-mp="h8ft276y1uat">
              {course.code} — {course.title}
            </h3>
            <p className="cz-num mt-0.5 text-[12px] text-ink-muted" data-id="element-2833" data-mp="3fxejf6kwrm15">
              {course.lecturer} · {course.units} units ·{' '}
              {topicsDone} of {learning.topics.length} topics complete
              {nextQuiz ? ` · next quiz ${nextQuiz.dueLabel?.replace('Due ', '')}` : ''}
            </p>
          </div>
          <div className="w-full shrink-0 sm:w-64" data-id="element-2834" data-mp="1xygmz34upsbp">
            <Meter label="Learning progress" value={progress} trailing={`${progress}%`} data-id="element-2835" data-mp="1t0ouo80ao46z" />
            <Button size="sm" variant="secondary" className="mt-2.5 w-full" icon={<PenLineIcon className="h-3.5 w-3.5" data-id="element-2837" data-mp="1j43qtq00gz4q" />} onClick={() => {
            createNote({
              template: 'lecture',
              title: `${course.code} — lecture note`,
              academicLink: {
                kind: 'Course',
                label: `${course.code} — ${course.title}`
              }
            });
            navigate('/notes');
          }} data-id="element-2836" data-mp="1o2wtendrf5q4">
              New note for this course
            </Button>
          </div>
        </div>
        <div className="mt-3 grid gap-3 border-t border-line pt-3 sm:grid-cols-3" data-id="element-2838" data-mp="vdg3bwdm40x2">
          <p className="cz-num text-[12px] text-ink-muted" data-id="element-2839" data-mp="2vg8zxe157g0d">
            <span className="block text-[13px] font-semibold text-ink" data-id="element-2840" data-mp="2vgqzokn0kcd1">
              {watchedMinutes} / {lectureMinutes} min
            </span>
            lectures watched
          </p>
          <p className="cz-num text-[12px] text-ink-muted" data-id="element-2841" data-mp="2lpmlvo92qxjs">
            <span className="block text-[13px] font-semibold text-ink" data-id="element-2842" data-mp="2qoqzcr317hfx">
              {quizAverage === null ? 'No attempts' : `${quizAverage}%`}
            </span>
            quiz average
          </p>
          <p className="cz-num text-[12px] text-ink-muted" data-id="element-2843" data-mp="2houel0jrj2q5">
            <span className="block text-[13px] font-semibold text-ink" data-id="element-2844" data-mp="2hm7w45vc32t5">
              {reviewed} / {learning.materials.length}
            </span>
            materials reviewed
          </p>
        </div>
        <p className="mt-3 border-t border-line pt-3 text-[12px] leading-5 text-ink-muted" data-id="element-2845" data-mp="27s7sk8fpa5mj">
          This is Campuzon learning progress, derived from your activity here. It is not a university
          grade — released results stay in Results, sourced from {course.lecturer}'s department.
        </p>
      </div>

      <Tabs tabs={TABS} value={tab} onChange={setTab} label={`${course.code} learning sections`} data-id="element-2846" data-mp="1li95n669gfqg" />

      {tab === 'Materials' && <Panel title="Course materials" bodyClassName="p-0" data-id="element-2847" data-mp="207x5s42ph8cl">
          <ul className="divide-y divide-line" data-id="element-2848" data-mp="1vgf75mo5njus">
            {learning.materials.map(material => <li key={material.id} className="flex flex-wrap items-center gap-3 px-5 py-3.5" data-id="element-2849" data-mp="297a9sh8294px">
                <FileTextIcon className="h-4 w-4 shrink-0 text-ink-subtle" data-id="element-2850" data-mp="2568hylgh3uxz" />
                <div className="min-w-0 flex-1" data-id="element-2851" data-mp="258b6f11z9g0l">
                  <div className="flex flex-wrap items-center gap-2" data-id="element-2852" data-mp="2j4onwa6ihhf2">
                    <p className="text-[13px] font-medium text-ink" data-id="element-2853" data-mp="2e6wlgm88qcwm">{material.title}</p>
                    <Badge data-id="element-2854" data-mp="orp382n9xx0x">{material.kind}</Badge>
                    {material.source === 'lecturer' ? <Badge tone="ok" icon={<BadgeCheckIcon className="h-3.5 w-3.5" data-id="element-2856" data-mp="jtf81s1juwfb" />} data-id="element-2855" data-mp="jsvn02glrrx2">
                        Published by lecturer
                      </Badge> : <Badge tone="neutral" icon={<PencilIcon className="h-3.5 w-3.5" data-id="element-2858" data-mp="2ot6h8ecp9zt2" />} data-id="element-2857" data-mp="2otyerupuy7lz">
                        Entered by you
                      </Badge>}
                  </div>
                  <p className="cz-num mt-0.5 text-[12px] text-ink-muted" data-id="element-2859" data-mp="2xumoid87xlt2">
                    {material.addedBy} · {material.addedOn} · {material.size}
                  </p>
                </div>
                {material.marketplaceResourceId ? <Link to="/marketplace" className="shrink-0" data-id="element-2860" data-mp="37r9bp8o0ooy9">
                    <Button size="sm" variant="secondary" data-id="element-2861" data-mp="3cj0ugx0xlqdu">
                      Open licensed copy
                    </Button>
                  </Link> : <Button size="sm" variant="secondary" className="shrink-0" data-id="element-2862" data-mp="3lk8qt9jw0h5a">
                    Open
                  </Button>}
              </li>)}
          </ul>
          <p className="border-t border-line px-5 py-3 text-[12px] leading-5 text-ink-muted" data-id="element-2863" data-mp="3qgz2lkonsqkw">
            Paid resources stay in the Knowledge marketplace with their licence and entitlement
            intact — access here ends when the rental period does.
          </p>
        </Panel>}

      {tab === 'Lectures' && <Panel title="Recorded lectures" bodyClassName="p-0" data-id="element-2864" data-mp="3mnzagy4725uu">
          <ul className="divide-y divide-line" data-id="element-2865" data-mp="3mpk3ukervir4">
            {learning.lectures.map(lecture => {
          const watched = percent(lecture.watchedMinutes, lecture.durationMinutes);
          return <li key={lecture.id} className="flex flex-wrap items-center gap-4 px-5 py-3.5" data-id="element-2866" data-mp="dij6rz6yiznh">
                  <div className="min-w-0 flex-1" data-id="element-2867" data-mp="9gxlbpgcxd1o">
                    <p className="text-[13px] font-medium text-ink" data-id="element-2868" data-mp="9g5zv7gh4vp4">{lecture.title}</p>
                    <p className="cz-num mt-0.5 text-[12px] text-ink-muted" data-id="element-2869" data-mp="15zvimz9qoeq2">
                      {lecture.lecturer} · recorded {lecture.recordedOn} ·{' '}
                      {lecture.durationMinutes} min
                    </p>
                  </div>
                  <div className="w-full sm:w-44" data-id="element-2870" data-mp="1kub5k5y82bvb">
                    <Meter value={watched} label={watched === 100 ? 'Watched' : watched === 0 ? 'Not started' : 'In progress'} trailing={`${watched}%`} tone={watched === 100 ? 'ok' : 'brand'} height="thin" data-id="element-2871" data-mp="1pnnqo63p00jb" />
                  </div>
                  <Button size="sm" variant={watched === 100 ? 'secondary' : 'primary'} className="shrink-0" icon={<PlayIcon className="h-3.5 w-3.5" data-id="element-2873" data-mp="1tqttf3rlj9l1" />} data-id="element-2872" data-mp="28lgiodcs2q1x">
                    {watched === 0 ? 'Watch' : watched === 100 ? 'Rewatch' : 'Resume'}
                  </Button>
                </li>;
        })}
          </ul>
        </Panel>}

      {tab === 'Quizzes' && (activeQuiz ? <QuizRunner quiz={activeQuiz} onExit={() => setActiveQuizId(null)} data-id="element-2874" data-mp="31fn14g09hx87" /> : <Panel title="Quizzes and practice" bodyClassName="p-0" data-id="element-2875" data-mp="2whbpq0ubjenv">
            <ul className="divide-y divide-line" data-id="element-2876" data-mp="2wj60wnfsfser">
              {learning.quizzes.map(quiz => <li key={quiz.id} className="flex flex-wrap items-center gap-3 px-5 py-3.5" data-id="element-2877" data-mp="3aeqj1gnp06l4">
                  <div className="min-w-0 flex-1" data-id="element-2878" data-mp="36d50beoh8y30">
                    <div className="flex flex-wrap items-center gap-2" data-id="element-2879" data-mp="2iovmayxi9emf">
                      <p className="text-[13px] font-medium text-ink" data-id="element-2880" data-mp="2dqk7saw9mfxv">{quiz.title}</p>
                      <Badge tone={quiz.scored ? 'brand' : 'neutral'} data-id="element-2881" data-mp="2dor2p01aw3rz">
                        {quiz.scored ? 'Scored' : 'Practice'}
                      </Badge>
                      {quiz.dueLabel && <Badge tone="warn" data-id="element-2882" data-mp="2mpywp5wsaig9">{quiz.dueLabel}</Badge>}
                    </div>
                    <p className="cz-num mt-0.5 text-[12px] text-ink-muted" data-id="element-2883" data-mp="1z04kmqkagxx2">
                      {quiz.questions.length} questions ·{' '}
                      {quiz.attemptsAllowed === null ? `${quiz.attemptsUsed} attempts taken · unlimited` : `${quiz.attemptsUsed} of ${quiz.attemptsAllowed} attempts used`}
                      {quiz.bestScore !== null ? ` · best ${quiz.bestScore}%` : ''}
                    </p>
                  </div>
                  <Button size="sm" variant="primary" className="shrink-0" disabled={quiz.attemptsAllowed !== null && quiz.attemptsUsed >= quiz.attemptsAllowed} onClick={() => setActiveQuizId(quiz.id)} data-id="element-2884" data-mp="qognzjdz8j1x">
                    {quiz.attemptsUsed > 0 ? 'Start another attempt' : 'Start'}
                  </Button>
                </li>)}
            </ul>
          </Panel>)}

      {tab === 'Study groups' && <Panel title="Study groups" description="These are Community groups scoped to this course." bodyClassName="p-0" data-id="element-2885" data-mp="3u706hu6usajj">
          {learning.studyGroups.length === 0 ? <EmptyState icon={<UsersIcon className="h-4 w-4" data-id="element-2887" data-mp="2elg0oh5s31zk" />} title="No study group yet" description="Nobody has started one for this course. Creating it opens a Community group with the same messaging, moderation and reporting." action={<Link to="/community" data-id="element-2888" data-mp="1qxop5fs2aszc">
                  <Button variant="secondary" data-id="element-2889" data-mp="1qyq4hwg421mv">Start a study group</Button>
                </Link>} data-id="element-2886" data-mp="2em7y7xixr9sh" /> : <ul className="divide-y divide-line" data-id="element-2890" data-mp="1zxv290julqxz">
              {learning.studyGroups.map(group => <li key={group.id} className="flex flex-wrap items-center gap-3 px-5 py-3.5" data-id="element-2891" data-mp="24obfokno5ckc">
                  <div className="min-w-0 flex-1" data-id="element-2892" data-mp="186x9xxle88d1">
                    <p className="text-[13px] font-medium text-ink" data-id="element-2893" data-mp="1865p9htc3v1j">{group.name}</p>
                    <p className="cz-num mt-0.5 text-[12px] text-ink-muted" data-id="element-2894" data-mp="1c77hvfsqx8uj">
                      {group.members} members · next session {group.nextSession}
                    </p>
                  </div>
                  <Link to="/community" className="shrink-0" data-id="element-2895" data-mp="2ajacaik5jw8t">
                    <Button size="sm" variant="secondary" data-id="element-2896" data-mp="2p8pw1el9n1f0">
                      Open in Community
                    </Button>
                  </Link>
                </li>)}
            </ul>}
        </Panel>}

      {tab === 'Discussion' && <Panel title="Discussion" description="Grouped by topic. Moderated by the course owner and the Campuzon trust queue." bodyClassName="p-0" data-id="element-2897" data-mp="1wm57sn0375fo">
          <ul className="divide-y divide-line" data-id="element-2898" data-mp="25m1q86vvv82a">
            {learning.discussion.map(post => <li key={post.id} className="px-5 py-4" data-id="element-2899" data-mp="21ic3lmyrr0jw">
                <div className="flex flex-wrap items-center gap-2" data-id="element-2900" data-mp="21lriaooj67ks">
                  <Badge data-id="element-2901" data-mp="3gykqtqrw7jb2">{post.topic}</Badge>
                  {post.resolvedBy && <Badge tone="ok" data-id="element-2902" data-mp="3czbt6atqf1a8">Answered by {post.resolvedBy}</Badge>}
                  <span className="cz-num ml-auto text-[12px] text-ink-subtle" data-id="element-2903" data-mp="3ki336yka45n5">{post.at}</span>
                </div>
                <p className="mt-2 text-[13px] leading-6 text-ink" data-id="element-2904" data-mp="3phpf94odffud">{post.body}</p>
                <p className="cz-num mt-1 text-[12px] text-ink-muted" data-id="element-2905" data-mp="3ajx2gka7l5jv">
                  {post.author} · @{post.handle} · {post.replies} repl
                  {post.replies === 1 ? 'y' : 'ies'}
                </p>
                <div className="mt-2.5 flex flex-wrap items-center justify-between gap-3 border-t border-line pt-2.5" data-id="element-2906" data-mp="72n7i4qk73un">
                  <Button size="sm" variant="secondary" icon={<MessageSquareIcon className="h-3.5 w-3.5" data-id="element-2908" data-mp="c4459rdqya70" />} data-id="element-2907" data-mp="c187q45t9nj3">
                    Reply
                  </Button>
                  <ReportControls subject={`${post.author} — ${post.topic}`} surface="message" data-id="element-2909" data-mp="2c6sig73ba66" />
                </div>
              </li>)}
          </ul>
        </Panel>}
    </div>;
}